<script setup>
import { reactive, ref, onMounted, onUnmounted } from 'vue';

// // Store management imports
import { useBreakStore } from '@/stores/break';

// // Utils imports
import { logDebug } from '@/utils/logger';

// // Module imports
import PollBranding from './PollBranding.vue';
import PollGoods from './PollGoods.vue';
import { PollSimpleOptions } from './pollSimpleUtils';

// // Component imports
import FluxButton from '@/components/FluxButton/FluxButtonComp.vue';

// // Asset imports
import pollSelectionCheckSrc from '@/assets/images/simple-polls/results-s-poll-selection-check.png';

const storeBreak = useBreakStore();

// Add a ref for updated percentages
const updatedPercentages = ref(null);
let updateTimeout = null;

const props = defineProps({
  emojiOnly: {
    type: Boolean,
    default: false,
  },
  outputBoxBackground: {
    type: Object,
    // required: true,
    default: reactive({
      background: 'darkmagenta',
    }),
  },
  hasPollHeader: {
    type: Boolean,
    default: false,
  },
  hasDismissal: {
    type: Boolean,
    default: false,
  },
  hasProgress: {
    type: Boolean,
    default: false,
  },
  hasProgressBegun: {
    type: Boolean,
    default: false,
  },
  hasOrbs: {
    type: Boolean,
    default: false,
  },
  isExcludingCheck: {
    type: Boolean,
    default: false,
  },
  isFocusOnDismiss: {
    type: Boolean,
    default: false,
  },
  adVoteState: {
    type: String,
    default: '',
  },
  adHeadline: {
    type: String,
    default: '',
  },
  adHeadlineColor: {
    type: String,
    default: 'rgba(11, 11, 11, 1)',
  },
  adTagline: {
    type: String,
    default: '',
  },
  adTaglineColor: {
    type: String,
    default: 'rgba(11, 11, 11, 1)',
  },
  preBrandCopy: {
    type: String,
    // required: true,
    default: 'Sponsored by',
  },
  pollOptions: {
    type: Array,
    // required: true,
    default: () => [
      {
        optionEntry: '1️⃣ One',
        optionLabel: PollSimpleOptions.OPTION1,
        optionIndex: 0,
        optionPosition: 1,
      },
      {
        optionEntry: '2️⃣ Two',
        optionLabel: PollSimpleOptions.OPTION2,
        optionIndex: 1,
        optionPosition: 2,
      },
      {
        optionEntry: '3️⃣ Three',
        optionLabel: PollSimpleOptions.OPTION3,
        optionIndex: 2,
        optionPosition: 3,
      },
    ],
  },
  focusIndex: {
    type: Number,
    // required: true,
    default: 0,
  },
  pollPlacement: {
    type: String,
    // required: true,
    default: '',
  },
  brandLogos: {
    type: Array,
    default: () => [],
  },
  breakUuid: {
    type: String,
    // required: true,
    default: '',
  },
  pollPercentages: {
    type: Array,
    default: () => [],
  },
  barColor: {
    type: String,
    default: 'rgba(124, 57, 151, 1)', // Defaulting to the original purple color
  },
  resultsBoxBackgroundColor: {
    type: String,
    default: 'rgba(52, 45, 73, 1)', // Defaulting to the original dark blue-grey color
  },
  progressDuration: {
    type: Number,
    // Only rendered when hasProgress && hasProgressBegun; TempEmojiPoll/TempPIPPoll never set either.
    default: 0,
  },
});

const styleObjectHeadline = reactive({
  color: props.adHeadlineColor,
});

const styleObjectTagline = reactive({
  color: props.adTaglineColor,
});

// Function to get the percentages to display (either updated or original)
const getDisplayPercentages = () => {
  return updatedPercentages.value || props.pollPercentages;
};

// // PollOutput functions

const getEmoji = (stringParam) => {
  const emojiRegex = /\p{Emoji}/gu;
  const capturedEmoji = stringParam.match(emojiRegex);

  return capturedEmoji[0];
};

const getPerc = (inputParam) => {
  const percentages = getDisplayPercentages();
  return percentages[inputParam];
};

// Set up graceful update after component is mounted
onMounted(() => {
  logDebug(
    `PollOutput mounted — hasProgress: ${props.hasProgress}, hasProgressBegun: ${props.hasProgressBegun}`,
  );
  // Clean up any existing timeout
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
});

// // PollOutput lifecycle hooks

// Clean up timeout on unmount
onUnmounted(() => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
});
</script>

<template>
  <div
    class="poll-output"
    :class="{
      'poll-output--open': props.hasProgress && props.hasProgressBegun,
    }"
  >
    <div class="poll-output__container">
      <!-- <div class="poll-output__container-wrapper-branding"> -->
      <div
        class="poll-output__wrapper"
        :class="{
          'poll-output__wrapper--upper-left': props.pollPlacement === 'upper_left',
          'poll-output__wrapper--upper-right': props.pollPlacement === 'upper_right',
          'poll-output__wrapper--lower-left': props.pollPlacement === 'lower_left',
          'poll-output__wrapper--lower-right': props.pollPlacement === 'lower_right',
        }"
      >
        <div class="poll-output__backdrop" :style="outputBoxBackground"></div>

        <div class="poll-output__contents">
          <div v-if="props.hasPollHeader" class="poll-output__header">
            <span class="poll-output__header-copy">Poll</span>
          </div>

          <div class="poll-output__vote-state">
            <span class="poll-output__vote-state-copy">{{ props.adVoteState }}</span>
          </div>

          <div
            v-if="props.adTagline"
            class="poll-output__tagline out-tagline"
            :style="styleObjectTagline"
          >
            <span class="poll-output__tagline-copy">{{ props.adTagline }}</span>
          </div>
          <div
            v-else-if="props.adHeadline && !props.adTagline"
            class="poll-output__tagline out-headline"
            :style="styleObjectHeadline"
          >
            <span class="poll-output__tagline-copy">{{ props.adHeadline }}</span>
          </div>

          <!-- <div class="poll-output__separator" :style="styleObjectTagline">
              <img class="poll-output__divider" :src="overlayDividerHorizontalSrc" />
            </div> -->

          <div
            class="poll-output__results-row"
            :class="{
              'has-dismissal': props.hasDismissal,
            }"
            :style="styleObjectTagline"
          >
            <div
              v-for="item in props.pollOptions"
              :key="item.optionPosition"
              class="poll-output__outcome"
            >
              <div
                class="poll-output__option-button"
                :class="{ active: item.optionIndex === props.focusIndex }"
              >
                <FluxButton
                  v-if="props.emojiOnly"
                  :class="{ active: item.optionIndex === props.focusIndex }"
                  :button-copy="getEmoji(item.optionEntry)"
                />
                <div
                  v-else
                  class="poll-output__results"
                  :style="{ backgroundColor: props.resultsBoxBackgroundColor }"
                >
                  <div class="poll-output__option-graph">
                    <div
                      v-if="(props.hasProgress && props.hasProgressBegun) || !props.hasProgress"
                      class="poll-output__option-bargraph"
                      :style="{
                        '--widthValue': `${getDisplayPercentages()[item.optionIndex]}`,
                        'background': props.barColor,
                      }"
                    ></div>
                  </div>
                  <div
                    class="poll-output__final-options"
                    :class="{ active: item.optionIndex === props.focusIndex }"
                  >
                    <div class="poll-output__final-options--copy">
                      <img
                        v-if="item.optionIndex === props.focusIndex && !props.isExcludingCheck"
                        class="poll-output__final-options--check"
                        :src="pollSelectionCheckSrc"
                      />
                      {{ item.optionEntry }}
                    </div>

                    <div class="poll-output__final-options--percentage">
                      {{ getPerc(item.optionIndex) }}%
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="props.emojiOnly" class="poll-output__option-graph">
                <div
                  class="poll-output__option-bargraph"
                  :style="{
                    width: `${getPerc(item.optionIndex)}%`,
                    background: props.barColor,
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="props.hasDismissal"
          class="poll-output__dismissal"
          :class="{ active: props.isFocusOnDismiss }"
        >
          <span class="poll-output__dismissal-copy">Dismiss</span>
        </div>

        <div v-if="props.hasProgress && props.hasProgressBegun" class="poll-output__progress">
          <div
            class="poll-output__progress-bar"
            :class="{
              'poll-output__progress-bar--closed': !hasProgressBegun,
              'poll-output__progress-bar--open': hasProgressBegun,
            }"
            :style="{ '--progress-duration': `${props.progressDuration / 1000}s` }"
          ></div>
        </div>

        <PollBranding
          v-if="
            props.brandLogos &&
            props.brandLogos.length > 0 &&
            props.brandLogos[0] &&
            props.brandLogos[0].sponsorLogoImageSrc
          "
          :poll-pre-brand-copy="props.preBrandCopy"
          :poll-pre-brand-color="styleObjectTagline"
          :poll-brand-logos="props.brandLogos"
        />
      </div>

      <PollGoods
        v-if="props.emojiOnly && !props.pollPlacement"
        :goods-data="storeBreak.breakProducts"
        :interactive-break-uuid="storeBreak.breakUuid"
        :ad-id="storeBreak.breakGeneratedIdentifier"
      />
    </div>
  </div>
</template>
